"use client"

import Image from "next/image"
import Link from "next/link"
import { useEffect, useRef, useState } from "react"
import { TranslatedContent } from '@/shared/i18n/provider'

export interface NextArticleBarProps {
  href: string
  title: string
  storageKey: string
}

const SHOW_FROM_PERCENT = 40
const HIDE_FROM_PERCENT = 95

/** Figma `spine/다음 글 바 [fixed]`(node 6495:3191). */
export function NextArticleBar({ href, title, storageKey }: NextArticleBarProps) {
  const [progress, setProgress] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)
  const lastScrollY = useRef(0)

  const sessionStorageKey = `sisun:next-article-bar:${storageKey}`

  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(sessionStorageKey) === "dismissed") {
        let isMounted = true
        window.queueMicrotask(() => {
          if (isMounted) setIsDismissed(true)
        })
        return () => {
          isMounted = false
        }
      }
    } catch {
      // 저장소가 막힌 브라우저에서도 안내 바 자체는 정상 동작한다.
    }

    lastScrollY.current = Math.max(0, window.scrollY)
    let animationFrame: number | null = null

    const measure = () => {
      const currentScrollY = Math.max(0, window.scrollY)
      const scrollableHeight = Math.max(1, document.documentElement.scrollHeight - window.innerHeight)
      const nextProgress = Math.min(100, Math.max(0, (currentScrollY / scrollableHeight) * 100))
      const isScrollingDown = currentScrollY > lastScrollY.current
      const isScrollingUp = currentScrollY < lastScrollY.current

      setProgress(nextProgress)

      if (nextProgress < SHOW_FROM_PERCENT || nextProgress >= HIDE_FROM_PERCENT || isScrollingUp) {
        setIsVisible(false)
      } else if (isScrollingDown) {
        setIsVisible(true)
      }

      lastScrollY.current = currentScrollY
      animationFrame = null
    }

    const requestMeasure = () => {
      if (animationFrame === null) {
        animationFrame = window.requestAnimationFrame(measure)
      }
    }

    window.addEventListener("scroll", requestMeasure, { passive: true })
    window.addEventListener("resize", requestMeasure)
    requestMeasure()

    return () => {
      window.removeEventListener("scroll", requestMeasure)
      window.removeEventListener("resize", requestMeasure)
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame)
    }
  }, [sessionStorageKey])

  const dismiss = () => {
    setIsVisible(false)
    setIsDismissed(true)
    try {
      window.sessionStorage.setItem(sessionStorageKey, "dismissed")
    } catch {
      // 저장에 실패해도 현재 화면에서는 닫힌 상태를 유지한다.
    }
  }

  if (isDismissed) return null

  return (
    <TranslatedContent><aside
      className={`fixed bottom-0 left-1/2 z-40 flex w-full max-w-[375px] -translate-x-1/2 flex-col items-start border-t border-line-3 bg-surface-base pb-[max(20px,env(safe-area-inset-bottom))] transition-[transform,opacity] duration-200 ease-out md:max-w-[1120px] ${
        isVisible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-full opacity-0"
      } motion-reduce:transition-none`}
      aria-label="다음 글 바로가기"
      aria-hidden={!isVisible}
      data-node-id="6495:3201"
      data-testid="next-article-bar"
    >
      <div
        className="h-[2px] w-full overflow-hidden bg-line-1"
        role="progressbar"
        aria-label="글 읽기 진행률"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress)}
      >
        <span
          className="block h-full bg-accent-mint-400 transition-[width] duration-100 motion-reduce:transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="grid h-[44px] w-full grid-cols-[24px_minmax(0,1fr)_24px] items-center gap-[8px] overflow-hidden pl-[18px] pr-[4px] md:h-[52px] md:grid-cols-[32px_minmax(0,1fr)_32px] md:gap-[12px] md:px-[24px]">
        <p className="whitespace-nowrap text-[10.5px] font-bold leading-normal text-accent-mint-400">다음</p>

        <Link
          href={href}
          className="flex min-w-0 items-center gap-[8px]"
          tabIndex={isVisible ? 0 : -1}
        >
          <span className="min-w-0 flex-1 truncate text-[14px] font-bold leading-[20px] text-ink-1 md:text-[15px]">
            {title}
          </span>
          <Image
            src="/figma/next-article-bar/arrow-outlined.svg"
            alt=""
            width={12}
            height={24}
            className="h-[24px] w-[12px] shrink-0"
          />
        </Link>

        <button
          type="button"
          onClick={dismiss}
          className="flex size-[24px] items-center justify-center overflow-hidden text-[13px] font-normal leading-normal text-ink-4 md:size-[32px]"
          aria-label="다음 글 안내 닫기"
          tabIndex={isVisible ? 0 : -1}
        >
          ✕
        </button>
      </div>
    </aside></TranslatedContent>
  )
}
